import { useCallback, useEffect, useMemo, useState } from 'react';
import { DateRangeField } from './DateRangeField';
import { DenseList, DenseRow } from './ui/DenseList';
import { Button } from './ui/primitives';
import { useShellTier } from '../layouts/shellTier';
import { getErrorMessage } from '../api/client';
import { getResponsesHistory } from '../api/formSubmissions';
import { getUsers } from '../api/users';
import type { ResponseHistoryItem } from '../types/formSubmissions';
import type { FormTemplateListItem } from '../types/forms';
import type { Store } from '../types/stores';
import type { User } from '../types/users';
import { DEFAULT_STORE_TIMEZONE, dateToStoreISO } from '../utils/timezones';
import { ResponseDetailView } from './ResponseDetailView';
import { userDisplayNameOr } from '../utils/userDisplayName';

// Fase 25A-R: pestaña "Respuestas" = EVIDENCIA / HISTORIAL de respuestas enviadas (distinta de
// Supervisión=analítica y de "Mis envíos"=historial personal). OWNER (todo el negocio) / MANAGER (sus
// tiendas). SOLO LECTURA. El backend valida permiso/alcance; aquí solo se presenta.

const MAX_RANGE_DAYS = 366;
const toISODate = (d: Date) => dateToStoreISO(d);
function defaultRange(): { from: string; to: string } {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 29); // ~30 dias por defecto
  return { from: toISODate(from), to: toISODate(to) };
}
// SIEMPRE en la zona horaria de la TIENDA (patrón 17A-4), no la del dispositivo: con el teléfono en
// otra zona, la hora de envío y la del corte se veían corridas vs. el historial de cortes.
const fmtDateTime = (iso: string | null, timeZone: string) => (iso ? new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeStyle: 'short', timeZone }).format(new Date(iso)) : '—');
// Solo fecha (día del TURNO del corte): así identifica el dueño sus cortes en el historial.
const fmtDate = (iso: string | null, timeZone: string) => (iso ? new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeZone }).format(new Date(iso)) : '—');
const contextLabel = (c: 'MANUAL' | 'SHIFT_CLOSE') => (c === 'SHIFT_CLOSE' ? 'Corte de turno' : 'Manual');
// Antes anteponia firstName+lastName a fullName: el orden INVERSO al del resto de la app, asi que
// la misma persona podia leerse distinto aqui que en un documento. Ahora usa la fuente unica.
const userName = (u: User) => userDisplayNameOr(u);

interface AppliedFilters {
  from: string; to: string; storeId?: string; templateId?: string; respondentUserId?: string; context: 'MANUAL' | 'SHIFT_CLOSE' | 'ALL';
}

export function FormResponsesPanel({ stores, templates, initialTemplateId, canOpenShiftClose }: {
  stores: Store[];
  templates: FormTemplateListItem[];
  initialTemplateId?: string;
  canOpenShiftClose: boolean;
}) {
  const init = useMemo(defaultRange, []);
  const tier = useShellTier();
  const [from, setFrom] = useState(init.from);
  const [to, setTo] = useState(init.to);
  const [storeId, setStoreId] = useState('');
  const [templateId, setTemplateId] = useState(initialTemplateId ?? '');
  const [respondentUserId, setRespondentUserId] = useState('');
  const [context, setContext] = useState<'MANUAL' | 'SHIFT_CLOSE' | 'ALL'>('ALL');
  const [rangeError, setRangeError] = useState<string>();

  const [applied, setApplied] = useState<AppliedFilters>({ from: init.from, to: init.to, templateId: initialTemplateId || undefined, context: 'ALL' });
  const tzFor = (storeId: string) => stores.find((store) => store.id === storeId)?.timezone ?? DEFAULT_STORE_TIMEZONE;
  // Zona de la tienda de la respuesta abierta (el detalle formatea su fecha con ella).
  const [openTz, setOpenTz] = useState<string>(DEFAULT_STORE_TIMEZONE);
  const [rows, setRows] = useState<ResponseHistoryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [openId, setOpenId] = useState<string | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const limit = 25;

  // Lista de usuarios para el filtro de respondente (best-effort; si el rol no puede, se omite el filtro).
  useEffect(() => {
    let alive = true;
    getUsers().then((r) => { if (alive) setUsers(r.users); }).catch(() => { if (alive) setUsers([]); });
    return () => { alive = false; };
  }, []);

  const apply = () => {
    const f = new Date(`${from}T00:00:00Z`).getTime();
    const t = new Date(`${to}T00:00:00Z`).getTime();
    if (Number.isNaN(f) || Number.isNaN(t) || f > t) { setRangeError('Rango de fechas inválido.'); return; }
    if ((t - f) / 86_400_000 > MAX_RANGE_DAYS) { setRangeError(`El rango no puede exceder ${MAX_RANGE_DAYS} días.`); return; }
    setRangeError(undefined);
    setOffset(0);
    setApplied({ from, to, storeId: storeId || undefined, templateId: templateId || undefined, respondentUserId: respondentUserId || undefined, context });
  };

  const load = useCallback(async () => {
    setLoading(true); setError(undefined);
    try {
      const res = await getResponsesHistory({ ...applied, limit, offset });
      setRows(res.responses); setTotal(res.pagination.total);
    } catch (e) { setError(getErrorMessage(e, 'No fue posible cargar las respuestas.')); }
    finally { setLoading(false); }
  }, [applied, offset]);

  useEffect(() => { void load(); }, [load]);

  if (openId) {
    return <ResponseDetailView submissionId={openId} timeZone={openTz} stores={stores} onBack={() => setOpenId(null)} canOpenShiftClose={canOpenShiftClose} />;
  }

  return (
    <div className="form-responses">
      <fieldset className="form-supervision__filters">
        <legend>Filtros</legend>
        {/* P-075: sin «Todas las fechas»: esta pantalla exige un rango (de 366 días o menos) y antes la rechazaba al aplicar. */}
        <DateRangeField allowClear={false} from={from} to={to} onChange={(nextFrom, nextTo) => { setFrom(nextFrom); setTo(nextTo); }} />
        <label className="field"><span>Tienda</span>
          <select value={storeId} onChange={(e) => setStoreId(e.target.value)}>
            <option value="">Todas mis tiendas</option>
            {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
        <label className="field"><span>Formulario</span>
          <select value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
            <option value="">Todos</option>
            {templates.map((t) => <option key={t.id} value={t.id}>{t.name}{t.isActive ? '' : ' (inactivo)'}</option>)}
          </select>
        </label>
        {users.length > 0 && (
          <label className="field"><span>Respondió</span>
            <select value={respondentUserId} onChange={(e) => setRespondentUserId(e.target.value)}>
              <option value="">Todos</option>
              {users.map((u) => <option key={u.id} value={u.id}>{userName(u)}{u.isActive ? '' : ' (inactivo)'}</option>)}
            </select>
          </label>
        )}
        <label className="field"><span>Origen</span>
          <select value={context} onChange={(e) => setContext(e.target.value as 'MANUAL' | 'SHIFT_CLOSE' | 'ALL')}>
            <option value="ALL">Todos</option>
            <option value="MANUAL">Manual</option>
            <option value="SHIFT_CLOSE">Corte de turno</option>
          </select>
        </label>
        <button className="button button--primary" onClick={apply} type="button">Aplicar</button>
        {rangeError && <span className="form-supervision__rangeerr" role="alert">{rangeError}</span>}
      </fieldset>

      {error && <div className="alert alert--error" role="alert">{error}</div>}

      {loading ? <div className="inline-loading"><span className="spinner" />Cargando respuestas…</div> : rows.length === 0 ? (
        <div className="empty-panel"><strong>Sin respuestas</strong><span>No hay respuestas enviadas para los filtros seleccionados.</span></div>
      ) : (
        <>
          {/* P-076: un solo marcado. En el teléfono, filas compactas; en tableta y escritorio, la tabla. Antes había
              una tabla y una lista de tarjetas duplicadas en el DOM, alternadas por CSS a 640 px. */}
          {tier === 'compact' ? (
            <DenseList label="Respuestas" separated>
              {rows.map((r) => (
                <DenseRow
                  actions={<Button onClick={() => { setOpenTz(tzFor(r.storeId)); setOpenId(r.id); }} variant="ghost">Ver respuesta</Button>}
                  badge={r.context === 'SHIFT_CLOSE' && r.shiftCloseVoided ? <span className="form-badge form-badge--inactive" title="El corte relacionado fue anulado">corte anulado</span> : undefined}
                  key={r.id}
                  meta={[
                    `${r.storeName} · ${r.respondentName}`,
                    `${contextLabel(r.context)}${r.context === 'SHIFT_CLOSE' && r.shiftCloseAt ? ` ${r.shiftCloseFolio ?? ''} del ${fmtDate(r.shiftCloseAt, tzFor(r.storeId))}` : ''}${r.versionNumber ? ` · v${r.versionNumber}` : ''}`,
                  ]}
                  name={r.formName}
                  subtitle={fmtDateTime(r.submittedAt, tzFor(r.storeId))}
                  title={r.formName}
                />
              ))}
            </DenseList>
          ) : (
          <div className="data-table-wrap">
            <table className="data-table">
              <thead><tr><th>Fecha de envío</th><th>Formulario</th><th>Tienda</th><th>Respondió</th><th>Origen</th><th>Versión</th><th></th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>{fmtDateTime(r.submittedAt, tzFor(r.storeId))}</td>
                    <td><strong>{r.formName}</strong></td>
                    <td>{r.storeName}</td>
                    <td>{r.respondentName}</td>
                    <td>
                      {contextLabel(r.context)}
                      {r.context === 'SHIFT_CLOSE' && r.shiftCloseAt && <small className="form-responses__shift">{r.shiftCloseFolio ? `${r.shiftCloseFolio} · ` : ''}del {fmtDate(r.shiftCloseAt, tzFor(r.storeId))}</small>}
                      {r.context === 'SHIFT_CLOSE' && r.shiftCloseVoided && <span className="form-badge form-badge--inactive" title="El corte relacionado fue anulado"> corte anulado</span>}
                    </td>
                    <td>{r.versionNumber ? `v${r.versionNumber}` : '—'}</td>
                    <td><button className="text-button" onClick={() => { setOpenTz(tzFor(r.storeId)); setOpenId(r.id); }} type="button">Ver respuesta</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}

          {total > limit && (
            <div className="form-supervision__pager">
              <button className="text-button" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))} type="button">← Anteriores</button>
              <span>{offset + 1}–{Math.min(offset + rows.length, total)} de {total}</span>
              <button className="text-button" disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)} type="button">Siguientes →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
